import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Typography, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Tooltip, Chip, CircularProgress,
} from '@mui/material'
import { Edit, Check, Close } from '@mui/icons-material'
import { api } from '../services/api'
import { usePlantContext } from '../hooks/usePlantContext'
import { useAuth } from '../hooks/useAuth'
import {
  monthLabel, parseWorkingDays, saveBody, summary, vsWeekdays, canEditCalendar,
} from '../components/calendar/workingDays'

const GREEN = '#15803D'
const AMBER_BG = '#FFFBEB'

// FAR-82: the plant's working days, month by month. Every month planning uses
// is listed; months nobody has set are planned at the default (22) and marked
// "Assumed". Planners and Admins set a month's days and an optional note;
// plans pick it up the next time they recalculate (Base Plans never change).
export default function WorkingDays() {
  const { plant } = usePlantContext()
  const { user } = useAuth()
  const canEdit = canEditCalendar(user?.roles)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    api.get(`/capacity/calendar?plant=${encodeURIComponent(plant.plantCode)}`)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Could not load working days') })
    return () => { cancelled = true }
  }, [plant.plantCode, version])

  const months = useMemo(() => data?.months || [], [data])
  const defaultDays = data?.defaultDays ?? 22

  const save = useCallback(async (month, workingDays, note) => {
    const saved = await api.put(`/capacity/calendar/${encodeURIComponent(month)}`, saveBody(plant.plantCode, workingDays, note))
    setVersion((v) => v + 1)
    return saved
  }, [plant.plantCode])

  return (
    <Box sx={{ p: 3, maxWidth: 980 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Working Days</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {plant.name || plant.plantCode} · Working days per month drive days of supply and line capacity.
        A change is used the next time a plan recalculates; Base Plans keep the days they were built with.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <CircularProgress size={24} />}

      {data && (
        <>
          {data.horizonPartial && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing only months already set: the plan's months couldn't be loaded. Reload to see every month.
            </Alert>
          )}
          <Alert severity={months.some((m) => m.assumed) ? 'warning' : 'success'} sx={{ mb: 2 }} data-testid="wd-summary">
            {summary(months, defaultDays)}
            {!canEdit && ' · Only Planners and Admins can change working days.'}
          </Alert>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Working days</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Compared with</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Set by</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map((m) => (
                  <MonthRow key={m.month} month={m} canEdit={canEdit} onSave={save} defaultDays={defaultDays} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}

function MonthRow({ month, canEdit, onSave, defaultDays }) {
  const [editing, setEditing] = useState(false)
  const [days, setDays] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const start = () => {
    // An assumed month starts from its Mon–Fri count, not the flat default.
    setDays(String(month.assumed ? month.weekdays : month.workingDays))
    setNote(month.note || '')
    setErr(null)
    setEditing(true)
  }
  const commit = async () => {
    const parsed = parseWorkingDays(days, month.month)
    if (parsed.error) { setErr(parsed.error); return }
    setBusy(true)
    setErr(null)
    try {
      await onSave(month.month, parsed.value, note)
      setEditing(false)
    } catch (e) {
      setErr(e?.message || 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  const label = monthLabel(month.month)
  return (
    <TableRow data-testid={`wd-row-${month.month}`} sx={{ bgcolor: month.assumed ? AMBER_BG : undefined }}>
      <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
      <TableCell align="right" sx={{ width: 150 }}>
        {editing ? (
          <TextField size="small" value={days} autoFocus error={!!err} helperText={err || ' '}
            onChange={(e) => setDays(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
            inputProps={{ inputMode: 'numeric', 'aria-label': `${label} working days`, style: { textAlign: 'right', width: 48 } }} />
        ) : (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {month.assumed && (
              <Tooltip title={`Nothing set: planned at the default ${defaultDays} days`}>
                <Chip size="small" label="Assumed" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#FEF3C7', color: '#92400E' }} />
              </Tooltip>
            )}
            <Typography variant="body2" sx={{ fontWeight: 700, color: month.assumed ? 'text.secondary' : GREEN }}>
              {month.workingDays}
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell><Typography variant="caption" color="text.secondary">{vsWeekdays(month)}</Typography></TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        {editing ? (
          <TextField size="small" fullWidth value={note} placeholder="e.g. Thanksgiving + day after" helperText=" "
            onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 200, 'aria-label': `${label} note` }}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }} />
        ) : (
          <Typography variant="body2" color="text.secondary">{month.note || ''}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {month.updatedBy ? `${month.updatedBy} · ${new Date(month.updatedAt).toLocaleDateString()}` : month.assumed ? '' : month.source}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 96 }}>
        {canEdit && !editing && (
          <Tooltip title={`Set ${label}`}>
            <IconButton size="small" aria-label={`Set ${label} working days`} onClick={start}><Edit fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {editing && (
          <>
            <IconButton size="small" aria-label="Save" onClick={commit} disabled={busy} sx={{ color: GREEN }}>
              {busy ? <CircularProgress size={16} /> : <Check fontSize="small" />}
            </IconButton>
            <IconButton size="small" aria-label="Cancel" onClick={() => setEditing(false)} disabled={busy}>
              <Close fontSize="small" />
            </IconButton>
          </>
        )}
      </TableCell>
    </TableRow>
  )
}
